import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Ticket, Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { code: "", description: "", discount_pct: 10, allowed_email: "", max_uses: "", active: true, expires_at: "" };
const legacyFreeCouponAdminEmail = "admin@flixvps.com.br";

const couponAllowedEmail = (coupon) => {
  const email = (coupon.allowed_email || "").trim().toLowerCase();
  return Number(coupon.discount_pct) === 100 && email === legacyFreeCouponAdminEmail
    ? ""
    : email;
};

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deletingCode, setDeletingCode] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/coupons");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (c) => {
    setEditing(c.code);
    setForm({
      code: c.code, description: c.description || "",
      discount_pct: c.discount_pct, max_uses: c.max_uses ?? "",
      allowed_email: couponAllowedEmail(c),
      active: !!c.active, expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
    });
    setDialog(true);
  };

  const save = async () => {
    if (!form.code || !form.code.trim()) { toast.error("Código obrigatório"); return; }
    if (!form.discount_pct || form.discount_pct < 1 || form.discount_pct > 100) {
      toast.error("Desconto deve estar entre 1 e 100"); return;
    }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description || "",
      discount_pct: Number(form.discount_pct),
      max_uses: form.max_uses === "" || form.max_uses === null
        ? (Number(form.discount_pct) === 100 ? 1 : null)
        : Number(form.max_uses),
      allowed_email: form.allowed_email.trim().toLowerCase() || null,
      active: !!form.active,
      expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59Z").toISOString() : null,
    };
    try {
      if (editing) await api.put(`/admin/coupons/${editing}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success(editing ? "Cupom atualizado" : "Cupom criado");
      setDialog(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Apagar cupom "${c.code}"?\n\nOs pedidos que já usaram este cupom não serão afetados.`)) return;
    setDeletingCode(c.code);
    try {
      await api.delete(`/admin/coupons/${c.code}`);
      toast.success("Cupom apagado");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingCode(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-coupons-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">// admin · cupons</div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter">Cupons de desconto</h1>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-white text-black hover:bg-zinc-200" data-testid="admin-coupon-new">
          <Plus className="h-4 w-4 mr-2" /> Novo cupom
        </Button>
      </div>

      <div className="border border-white/10 bg-[#121214]">
        <table className="w-full text-sm">
          <thead className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr className="border-b border-white/10">
              <th className="text-left px-5 py-3">Código</th>
              <th className="text-left px-5 py-3">Descrição</th>
              <th className="text-left px-5 py-3">Desconto</th>
              <th className="text-left px-5 py-3">E-mail autorizado</th>
              <th className="text-left px-5 py-3">Usos</th>
              <th className="text-left px-5 py-3">Expira</th>
              <th className="text-left px-5 py-3">Ativo</th>
              <th className="text-right px-5 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.code} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-coupon-row-${c.code}`}>
                <td className="px-5 py-3 font-mono font-bold text-pink-300">{c.code}</td>
                <td className="px-5 py-3 text-zinc-400">{c.description || <span className="text-zinc-600">—</span>}</td>
                <td className="px-5 py-3 font-mono">{c.discount_pct}%</td>
                <td className="px-5 py-3 text-xs text-zinc-400">{couponAllowedEmail(c) || <span className="text-zinc-600">Qualquer cliente</span>}</td>
                <td className="px-5 py-3 font-mono text-xs">{c.uses}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}</td>
                <td className="px-5 py-3 font-mono text-xs">{c.expires_at ? fmtDate(c.expires_at) : <span className="text-zinc-600">—</span>}</td>
                <td className="px-5 py-3">
                  {c.active
                    ? <Badge className="rounded-sm bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">ativo</Badge>
                    : <Badge className="rounded-sm bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">inativo</Badge>}
                </td>
                <td className="px-5 py-3 text-right space-x-1">
                  <Button variant="outline" size="sm" onClick={() => openEdit(c)} className="rounded-sm border-white/20 hover:bg-white hover:text-black h-8 px-3" data-testid={`admin-coupon-edit-${c.code}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(c)} disabled={deletingCode === c.code} className="rounded-sm border-red-500/40 text-red-400 hover:bg-red-500 hover:text-white hover:border-red-500 h-8 px-3" data-testid={`admin-coupon-delete-${c.code}`}>
                    {deletingCode === c.code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} className="p-10 text-center text-zinc-500">Nenhum cupom cadastrado. Clique em "Novo cupom".</td></tr>
            )}
            {loading && (<tr><td colSpan={8} className="p-10 text-center text-zinc-500 font-mono text-xs">Carregando…</td></tr>)}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="font-heading tracking-tighter flex items-center gap-2"><Ticket className="h-5 w-5" /> {editing ? "Editar cupom" : "Novo cupom"}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Código</Label>
              <Input value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME20" className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono uppercase tracking-widest" data-testid="admin-coupon-code" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Descrição</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Cupom de boas-vindas · 20% OFF" className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="admin-coupon-desc" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">E-mail autorizado (opcional)</Label>
              <Input type="email" value={form.allowed_email} onChange={(e) => setForm({ ...form, allowed_email: e.target.value })} placeholder="Vazio libera para qualquer cliente" className="rounded-sm bg-[#09090B] border-white/10 mt-1" data-testid="admin-coupon-email" />
              <p className="mt-1 text-xs text-zinc-500">Deixe vazio para qualquer cliente usar. Informe um e-mail para limitar o cupom a uma conta.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Desconto (%)</Label>
                <Input type="number" min={1} max={100} value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" data-testid="admin-coupon-pct" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">
                  {Number(form.discount_pct) === 100 ? "Máx. usos (vazio = 1)" : "Máx. usos (vazio = ilimitado)"}
                </Label>
                <Input type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder={Number(form.discount_pct) === 100 ? "1 (padrão)" : "ilimitado"} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" data-testid="admin-coupon-maxuses" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Expira em (opcional)</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-sm bg-[#09090B] border-white/10 mt-1 font-mono" data-testid="admin-coupon-expires" />
            </div>
            <label className="flex items-center gap-2 select-none">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-white" data-testid="admin-coupon-active" />
              <span className="text-sm">Ativo</span>
            </label>
            <Button onClick={save} disabled={saving} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200" data-testid="admin-coupon-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvando…</> : (editing ? "Salvar alterações" : "Criar cupom")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
